<template>
    <v-card
        v-if="match"
        class="match-card rounded-lg"
        :class="{
            'match-card--postponed': match.postponed,
            'match-card--live': isLive,
            'match-card--mobile': $vuetify.breakpoint.mobile
        }"
        @click="goToMatch"
    >
        <div class="match-card__meta">
            <span v-if="showDate && match.matchDatetime" class="meta-date">
                <v-icon x-small color="orange">mdi-calendar-outline</v-icon>
                {{ getDate(match.matchDatetime) }}
            </span>
            <span v-else-if="match.matchDatetime" class="meta-date">
                <v-icon x-small color="orange">mdi-clock-outline</v-icon>
                {{ getTime(match.matchDatetime) }}
            </span>
            <span class="meta-chip">
                <v-icon v-if="match.cup || !match.league" x-small color="orange">mdi-trophy</v-icon>
                {{ match.league ? match.league.title : 'Κύπελλο' }}
            </span>
            <span v-if="match.round" class="meta-chip">Αγ. {{ match.round }}</span>
            <span class="meta-chip">{{ phaseLabel }}</span>
            <v-spacer />
            <span v-if="isLive" class="live-badge">
                <span class="live-badge__dot" />
                LIVE
            </span>
            <span v-if="match.postponed" class="postponed-badge">
                <v-icon x-small color="white">mdi-update</v-icon>
                ΑΝΑΒΟΛΗ
            </span>
        </div>

        <div class="match-card__main">
            <div class="team team--home" :class="teamResultClass(1)" @click.stop="goToTeam(match.team1)">
                <v-img
                    v-if="match.team1.logo"
                    :src="`${assetsUrl}/${match.team1.logo}`"
                    class="team__logo"
                    contain
                />
                <v-icon v-else class="team__logo team__logo--placeholder orange--text">mdi-basketball</v-icon>
                <span class="team__name">{{ match.team1.name }}</span>
            </div>

            <div class="match-center">
                <div v-if="hasScore || isLive" class="scoreboard" :class="{ 'scoreboard--live': isLive }">
                    <span class="scoreboard__score" :class="scoreClass(1)">{{ match.scoreTeam1 || 0 }}</span>
                    <span class="scoreboard__sep">:</span>
                    <span class="scoreboard__score" :class="scoreClass(2)">{{ match.scoreTeam2 || 0 }}</span>
                </div>
                <div v-else-if="match.postponed" class="match-time match-time--postponed">
                    –:–
                </div>
                <div v-else class="match-time">
                    {{ getTime(match.matchDatetime) }}
                </div>
                <span v-if="!hasScore && !isLive && !match.postponed" class="match-center__vs">VS</span>
            </div>

            <div class="team team--away" :class="teamResultClass(2)" @click.stop="goToTeam(match.team2)">
                <v-img
                    v-if="match.team2.logo"
                    :src="`${assetsUrl}/${match.team2.logo}`"
                    class="team__logo"
                    contain
                />
                <v-icon v-else class="team__logo team__logo--placeholder orange--text">mdi-basketball</v-icon>
                <span class="team__name">{{ match.team2.name }}</span>
            </div>
        </div>

        <div class="match-card__footer">
            <v-btn
                v-if="match.arena"
                text
                small
                class="arena-btn"
                :disabled="!match.arena.coordinates"
                @click.stop="openMap(match.arena)"
            >
                <v-icon small color="orange" class="arena-btn__icon">mdi-basketball-hoop-outline</v-icon>
                <span class="arena-btn__name">{{ match.arena.name }}</span>
            </v-btn>
            <v-spacer />
            <v-btn v-if="!match.pending && match.videoUrl" icon small title="Video" @click.stop="openVideo">
                <v-icon color="red">mdi-youtube</v-icon>
            </v-btn>
            <v-btn v-if="!match.postponed" icon small title="Λεπτομέρειες αγώνα" @click.stop="goToMatch">
                <v-icon color="orange">mdi-arrow-right-thin-circle-outline</v-icon>
            </v-btn>
        </div>
    </v-card>
</template>

<script>
    import momentTimezone from 'moment-timezone'
    import { isMatchLive, refreshLiveStatuses, CHECK_AFTER_START_MINUTES, LIVE_TICK_MS } from '@/utils/matchLive'

    const PHASE_LABELS = {
        regular_season: 'Regular Season',
        placements: 'Προκριματικά',
        playoffs: 'Playoffs'
    }

    export default {
        props: {
            match: {
                type: Object,
                default: null
            },
            showDate: {
                type: Boolean,
                default: true
            }
        },
        data() {
            return {
                assetsUrl: 'https://storage.googleapis.com',
                now: Date.now(),
                timer: null
            }
        },
        computed: {
            hasScore() {
                return !this.match.pending && !this.match.postponed && !(this.match.scoreTeam1 === 0 && this.match.scoreTeam2 === 0)
            },
            isLive() {
                return isMatchLive(this.match, this.now)
            },
            phaseLabel() {
                if (this.match.cup) {
                    return 'Κύπελλο'
                }
                return PHASE_LABELS[this.match.phase] || 'Playoffs'
            }
        },
        created() {
            if (!this.match || this.match.postponed || !this.match.matchDatetime) {
                return
            }
            refreshLiveStatuses([this.match])

            // Only matches that have not passed their live check window can still change state
            const checkWindowEnd = momentTimezone(this.match.matchDatetime).valueOf() + CHECK_AFTER_START_MINUTES * 60 * 1000
            if (checkWindowEnd > Date.now()) {
                this.timer = setInterval(() => {
                    this.now = Date.now()
                    refreshLiveStatuses([this.match], this.now)
                }, LIVE_TICK_MS)
            }
        },
        beforeDestroy() {
            clearInterval(this.timer)
        },
        methods: {
            getDate(date) {
                return momentTimezone(date).tz('Europe/Athens').locale('el').format('dddd D MMMM YYYY, HH:mm')
            },
            getTime(date) {
                return date ? momentTimezone(date).tz('Europe/Athens').format('HH:mm') : '–:–'
            },
            scoreClass(team) {
                if (!this.hasScore || this.isLive || this.match.scoreTeam1 === this.match.scoreTeam2) {
                    return ''
                }
                const own = team === 1 ? this.match.scoreTeam1 : this.match.scoreTeam2
                const other = team === 1 ? this.match.scoreTeam2 : this.match.scoreTeam1
                return own > other ? 'scoreboard__score--winner' : 'scoreboard__score--loser'
            },
            teamResultClass(team) {
                if (!this.hasScore || this.isLive || this.match.scoreTeam1 === this.match.scoreTeam2) {
                    return ''
                }
                const own = team === 1 ? this.match.scoreTeam1 : this.match.scoreTeam2
                const other = team === 1 ? this.match.scoreTeam2 : this.match.scoreTeam1
                return own > other ? 'team--winner' : 'team--loser'
            },
            goToMatch() {
                if (this.match.postponed) {
                    return
                }
                this.$router.push({ name: 'Match', params: { id: this.match.id } })
            },
            openMap(arena) {
                if (!arena || !arena.coordinates) {
                    return
                }
                window.open(`https://www.google.com/maps/place/${arena.coordinates}`, '_blank', 'noopener,noreferrer')
            },
            openVideo() {
                window.open(this.match.videoUrl, '_blank', 'noopener,noreferrer')
            },
            goToTeam(team) {
                this.$router.push({ name: 'Team', params: { id: team.id } })
            }
        }
    }
</script>

<style scoped>
.match-card {
    padding: 12px 16px 8px;
    font-family: Ubuntu, Arial, sans-serif;
    border-left: 4px solid transparent;
    transition: transform 0.15s ease, box-shadow 0.15s ease;
    cursor: pointer;
}

.match-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 22px rgba(0, 0, 0, 0.14) !important;
}

.match-card--live {
    border-left-color: #ef4444;
}

.match-card--postponed {
    cursor: default;
    opacity: 0.7;
}

.match-card--postponed:hover {
    transform: none;
}

/* ---------- Meta ---------- */
.match-card__meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    font-size: 0.75rem;
}

.meta-date {
    font-weight: 700;
    margin-right: 6px;
    text-transform: capitalize;
}

.meta-chip {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 1px 8px;
    border-radius: 10px;
    background: #f1f1f3;
    color: rgba(0, 0, 0, 0.7);
    font-weight: 600;
}

.live-badge,
.postponed-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 8px;
    border-radius: 4px;
    color: #fff;
    font-weight: 800;
    letter-spacing: 0.08em;
}

.live-badge {
    background: #ef4444;
}

.live-badge__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #fff;
    animation: live-pulse 1.2s ease-in-out infinite;
}

.postponed-badge {
    background: #616161;
}

@keyframes live-pulse {
    50% {
        opacity: 0.25;
    }
}

/* ---------- Main ---------- */
.match-card__main {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    gap: 16px;
    padding: 12px 0 6px;
}

.team {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
    font-weight: 700;
    font-size: 1.05rem;
}

.team--home {
    flex-direction: row-reverse;
    text-align: right;
}

.team__logo {
    flex: 0 0 48px;
    width: 48px;
    height: 48px;
    font-size: 44px !important;
}

.team__name {
    line-height: 1.2;
    word-break: break-word;
    transition: color 0.15s ease;
}

.team:hover .team__name {
    color: #ef4444;
}

.team--loser {
    opacity: 0.55;
    font-weight: 500;
}

.match-center {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
}

.match-center__vs {
    font-size: 0.65rem;
    font-weight: 800;
    letter-spacing: 0.2em;
    color: rgba(0, 0, 0, 0.4);
}

.scoreboard {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 4px;
    border-radius: 8px;
    background: #0d0d0f;
}

.scoreboard__score {
    min-width: 46px;
    padding: 4px 8px;
    border-radius: 6px;
    color: #fff;
    font-size: 1.4rem;
    font-weight: 800;
    text-align: center;
    font-variant-numeric: tabular-nums;
}

.scoreboard__score--winner {
    background: linear-gradient(90deg, #ef4444 0%, #ff7133 50%, orange 100%);
}

.scoreboard__score--loser {
    color: rgba(255, 255, 255, 0.55);
}

.scoreboard__sep {
    color: rgba(255, 255, 255, 0.4);
    font-weight: 800;
}

.scoreboard--live {
    box-shadow: 0 0 0 2px #ef4444, 0 0 14px rgba(239, 68, 68, 0.5);
}

.match-time {
    padding: 6px 14px;
    border-radius: 8px;
    background: #f1f1f3;
    font-size: 1.3rem;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
}

.match-time--postponed {
    color: rgba(0, 0, 0, 0.4);
    text-decoration: line-through;
}

/* ---------- Footer ---------- */
.match-card__footer {
    display: flex;
    align-items: center;
    gap: 2px;
    margin-top: 4px;
    padding-top: 4px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.arena-btn {
    min-width: 0 !important;
    max-width: 70%;
    padding: 0 6px !important;
    text-transform: none;
    letter-spacing: normal;
    font-weight: 600;
}

.arena-btn ::v-deep .v-btn__content {
    min-width: 0;
    max-width: 100%;
}

.arena-btn__icon {
    flex: 0 0 auto;
    margin-right: 6px;
}

.arena-btn__name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* ---------- Mobile ---------- */
.match-card--mobile {
    padding: 10px 10px 6px;
}

.match-card--mobile .match-card__main {
    gap: 8px;
}

.match-card--mobile .team,
.match-card--mobile .team--home {
    flex-direction: column;
    text-align: center;
    gap: 6px;
    font-size: 0.9rem;
}

.match-card--mobile .team__logo {
    flex-basis: 40px;
    width: 40px;
    height: 40px;
    font-size: 36px !important;
}

.match-card--mobile .scoreboard__score {
    min-width: 36px;
    font-size: 1.15rem;
    padding: 3px 6px;
}

.match-card--mobile .match-time {
    font-size: 1.1rem;
    padding: 4px 10px;
}

@media (prefers-reduced-motion: reduce) {
    .live-badge__dot {
        animation: none;
    }
}
</style>
